import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchChartListBySourceId,
  getReportSourceById,
} from '@services/api/wizardAPI'
import {
  IReportBlueprint,
  IReportComponentBlueprint,
} from '@decoders/blueprintDecoder'
import { v4 as uuid } from 'uuid'

export const useFetchStructureQuery = (sourceId: string) => {
  const queryClient = useQueryClient()
  return useQuery(
    ['reportSourceStructure', sourceId],
    () => getReportSourceById(sourceId),
    {
      select: mapDataToSection,
      initialData: {
        children: [] as unknown as IReportComponentBlueprint[],
      } as IReportBlueprint,
      enabled: !!sourceId,
    },
  )
}

export const useFetchChartListQuery = (
  sourceId: string,
  reportBlueprintId: string,
) => {
  return useQuery(
    ['reportSourceChartList', sourceId],
    () => fetchChartListBySourceId(sourceId),
    {
      select: mapDataToChart,
      initialData: [],
      enabled: !!reportBlueprintId,
    },
  )
}

export const useFetchReportSections = (sourceId: string) => {
  const { data, isLoading, isError, error } = useQuery(
    ['reportSourceStructure', sourceId],
    () => getReportSourceById(sourceId),
    {
      select: (data) => mapDataToSection(data),
      initialData: { children: [] as unknown as IReportComponentBlueprint[] } as IReportBlueprint,
      enabled: !!sourceId,
    },
  )

  // Transform the data into the desired format if data is available
  const reportSections = data?.children?.map((section: IReportComponentBlueprint) => ({
    title: section.name,
    id: section.id,
    filters: section.filters,
  })) || []

  // Return the transformed data along with the loading and error states
  return { reportSections, isLoading, isError, error }
}

function mapDataToSection(data: IReportBlueprint): IReportBlueprint {
  return {
    ...data,
    children: (data.components as IReportComponentBlueprint[])?.map(
      component => ({
        ...component,
        componentType: 0,
        type: 'section',
        children: component.children.map(child => recursiveType(child)),
      }),
    ),
  }
}

function mapDataToChart(chartList: any[]) {
  return chartList.map(chart => ({
    ...chart,
    name: chart.chartCollectionName,
    id: uuid(),
    rows: chart.protoCharts[0].rows.map(row => row.name),
    columns: chart.protoCharts[0].columns.map(column => column.name),
    drillDownInfo: chart.protoCharts[0].drillDownInfo,
    type: 'chart',
  }))
}

const recursiveType = node => {
  if (node.children) {
    node.children = node.children.map(child => recursiveType(child))
    node.type = node.componentType === 1 ? 'subsection' : 'chart'
  }
  return node
}
